<!-- Dine-In switched off (FNF-1223): guests cannot start or join a table. -->
@if (dineInOff) {
  <div class="tbl-dine-in-off" role="status">
    <i class="bx bx-error" aria-hidden="true"></i>
    <span class="tbl-dine-in-off-text">
      @if (dineInOff.requiredPlan) {
        Dine-In is available on the {{ dineInOff.requiredPlan }} plan. Guests can't start a table
        until you upgrade.
      } @else {
        Dine-In is turned off. Guests can't start a table or order from it until you turn it on.
      }
    </span>
    @if (canOpenSettings) {
      <a class="tbl-dine-in-off-action" routerLink="/main/settings">Go to Settings</a>
    }
  </div>
}

<!-- Toolbar: search, view toggle, status filter, grouping, add. -->
<div class="tbl-toolbar">
  <div class="tbl-search">
    <i class="bx bx-search" aria-hidden="true"></i>
    <input
      type="text"
      placeholder="Search tables..."
      aria-label="Search tables"
      [(ngModel)]="searchQuery"
      (input)="filterTables()"
    />
  </div>

  <div class="fnf-segmented fnf-segmented--compact" role="tablist">
    <button
      type="button"
      class="fnf-segment"
      [class.fnf-segment--active]="view === 'list'"
      (click)="changeView('list')"
    >
      <i class="bx bx-list-ul" aria-hidden="true"></i> List
    </button>
    <button
      type="button"
      class="fnf-segment"
      [class.fnf-segment--active]="view === 'grid'"
      (click)="changeView('grid')"
    >
      <i class="bx bx-grid-alt" aria-hidden="true"></i> Grid
    </button>
    <button
      type="button"
      class="fnf-segment"
      [class.fnf-segment--active]="view === 'layout'"
      (click)="changeView('layout')"
    >
      <i class="bx bx-layout" aria-hidden="true"></i> Layout
    </button>
    <button
      type="button"
      class="fnf-segment"
      [class.fnf-segment--active]="view === 'combinations'"
      (click)="changeView('combinations')"
    >
      <i class="bx bx-merge" aria-hidden="true"></i> Combinations
    </button>
  </div>

  <!-- The floor plan and the combinations always show every table of a zone,
       so status and grouping filters only apply to the list and grid views. -->
  @if (view !== 'layout' && view !== 'combinations') {
    <div class="tbl-status-filters" role="group" aria-label="Filter by status">
      @for (filter of statusFilters; track filter.value) {
        <button
          type="button"
          class="tbl-status-filter"
          [class.tbl-status-filter--active]="statusFilter === filter.value"
          (click)="setStatusFilter(filter.value)"
        >
          {{ filter.label }}
        </button>
      }
    </div>

    <div class="tbl-groupby">
      <span class="tbl-groupby-label">Group by:</span>
      <div class="btn-group custom-dropdown1">
        <button
          type="button"
          class="tbl-groupby-btn"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          {{ groupBy === 'zone' ? 'Zone' : 'None' }}
          <i class="bx bx-chevron-down" aria-hidden="true"></i>
        </button>
        <ul class="dropdown-menu dropdown-menu-end">
          <li><a class="dropdown-item type-option" (click)="setGroupBy('zone')">Zone</a></li>
          <li><a class="dropdown-item type-option" (click)="setGroupBy('none')">None</a></li>
        </ul>
      </div>
    </div>
  }

  <button type="button" class="tbl-add-btn" (click)="createTableConfirm()">
    <i class="bx bx-plus" aria-hidden="true"></i> Add Table
  </button>
</div>

@if (displayLoading) {
  <div
    class="d-flex flex-column align-items-center justify-content-center common-loading-container"
  >
    <div class="w-25 text-center">
      <img class="common-loading" src="assets/menu_loading.gif" alt="common loading" />
      <p class="common-loading-text">Loading tables, please wait...</p>
    </div>
  </div>
} @else if (tablesLoadFailed) {
  <div
    class="d-flex flex-column align-items-center justify-content-center common-empty-container"
    role="alert"
  >
    <p class="common-empty-header">Couldn't load tables</p>
    <p class="common-empty-sub-text">
      Something went wrong on our side. Your tables haven't been deleted.
    </p>
    <button type="button" class="tbl-add-btn tbl-retry-btn" (click)="retryTables()">
      <i class="bx bx-refresh" aria-hidden="true"></i> Try again
    </button>
  </div>
} @else if (view === 'layout') {
  <!-- ---------------------------------------------------------- layout -->
  <app-floor-plan
    [tables]="unFilteredTables"
    [zones]="zones"
    [searchQuery]="searchQuery"
    (changed)="onLayoutChanged()"
  />
} @else if (view === 'combinations') {
  <!-- ---------------------------------------------------- combinations -->
  <app-table-combinations [tables]="unFilteredTables" [zones]="zones" />
} @else if (hasResults) {
  <!-- ------------------------------------------------------------ list -->
  @if (view === 'list') {
    @for (group of groups; track group.label) {
      @if (group.tables.length) {
        <section class="tbl-zone-card">
          <header class="tbl-zone-head">
            <h2 class="tbl-zone-title">{{ group.label }}</h2>
            <span class="tbl-zone-count">{{ group.tables.length }} Tables</span>
            @if (group.zoneId) {
              <button
                type="button"
                class="tbl-zone-edit"
                matTooltip="Edit zone"
                (click)="editZone(group)"
              >
                <i class="bx bx-edit-alt" aria-hidden="true"></i>
              </button>
            }
          </header>

          <div class="tbl-scroll">
            <table class="tbl-table">
              <thead>
                <tr>
                  <th scope="col" class="tbl-col-name">Name</th>
                  <th scope="col" class="tbl-col-shape">Shape</th>
                  @if (showStatusColumn) {
                    <th scope="col" class="tbl-col-status">Status</th>
                  }
                  @if (showZoneColumn) {
                    <th scope="col" class="tbl-col-zone">Zone</th>
                  }
                  <th scope="col" class="tbl-col-waiter">Waiter</th>
                  <th scope="col" class="tbl-col-qr">QR Code</th>
                  <th scope="col" class="tbl-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                @for (table of group.tables; track table.table_id || table.table_name) {
                  <tr>
                    <td class="tbl-name">{{ table.table_name }}</td>
                    <td>
                      <span class="tbl-shape">
                        <i class="bx" [ngClass]="shapeIcon(table)" aria-hidden="true"></i>
                        {{ shapeLabel(table) }}
                      </span>
                    </td>
                    @if (showStatusColumn) {
                      <td>
                        <span
                          class="tbl-status"
                          [ngClass]="'tbl-status--' + (table.status || 'unknown')"
                        >
                          <span class="tbl-status-dot" aria-hidden="true"></span>
                          {{ table.status | titlecase }}
                        </span>
                      </td>
                    }
                    @if (showZoneColumn) {
                      <td class="tbl-zone-cell">{{ table.zone?.zone_name || '—' }}</td>
                    }
                    <td class="tbl-muted">{{ waiterLabel(table) }}</td>
                    <td>
                      <div class="tbl-qr">
                        <button
                          type="button"
                          class="tbl-link"
                          data-bs-toggle="modal"
                          data-bs-target="#exampleModal"
                          (click)="viewQrCode(table)"
                        >
                          View
                        </button>
                        <div class="btn-group custom-dropdown1">
                          <button
                            type="button"
                            class="tbl-kebab"
                            data-bs-toggle="dropdown"
                            data-bs-display="static"
                            aria-expanded="false"
                            aria-label="QR code actions"
                          >
                            <i class="bx bx-dots-horizontal-rounded" aria-hidden="true"></i>
                          </button>
                          <ul class="dropdown-menu dropdown-menu-end">
                            <li>
                              <a class="dropdown-item type-option" (click)="downloadImage(table)">
                                <i class="bx bx-image menu-icon" aria-hidden="true"></i> Download as
                                Image
                              </a>
                            </li>
                            <li>
                              <a class="dropdown-item type-option" (click)="downloadAsPDF(table)">
                                <i class="bx bx-note menu-icon" aria-hidden="true"></i> Download as
                                PDF
                              </a>
                            </li>
                            <li>
                              <a class="dropdown-item type-option" (click)="printQRCode(table)">
                                <i class="bx bx-printer menu-icon" aria-hidden="true"></i> Print
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </td>
                    <td class="tbl-col-actions">
                      <div class="btn-group custom-dropdown1">
                        <button
                          type="button"
                          class="tbl-kebab"
                          data-bs-toggle="dropdown"
                          data-bs-display="static"
                          aria-expanded="false"
                          aria-label="Table actions"
                        >
                          <i class="bx bx-dots-horizontal-rounded" aria-hidden="true"></i>
                        </button>
                        <ul class="dropdown-menu dropdown-menu-end">
                          <li>
                            <a class="dropdown-item type-option" (click)="addNewTable(table)">
                              <i class="bx bx-edit-alt menu-icon" aria-hidden="true"></i>
                              Edit Table
                            </a>
                          </li>
                          @if (table.status !== 'available') {
                            <li>
                              <a
                                class="dropdown-item type-option tbl-item-success"
                                (click)="markAsAvailable(table)"
                              >
                                <i class="bx bx-check-circle menu-icon" aria-hidden="true"></i>
                                Mark as Available
                              </a>
                            </li>
                          }
                          <li>
                            <a
                              class="dropdown-item type-option delete-dropdown-item"
                              (click)="deleteTableConfirm(table)"
                            >
                              <i class="bx bx-trash menu-icon" aria-hidden="true"></i>
                              Delete Table
                            </a>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      }
    }
  }

  <!-- ------------------------------------------------------------ grid -->
  @if (view === 'grid') {
    @for (group of groups; track group.label) {
      @if (group.tables.length) {
        <section class="tbl-grid-section">
          <header class="tbl-grid-head">
            <h2 class="tbl-zone-title">{{ group.label }}</h2>
            <span class="tbl-zone-count">{{ group.tables.length }} Tables</span>
          </header>

          <div class="tbl-grid">
            @for (table of group.tables; track table.table_id || table.table_name) {
              <article class="tbl-card">
                <header class="tbl-card-head">
                  <h3 class="tbl-card-title">{{ table.table_name }}</h3>
                  <div class="btn-group custom-dropdown1">
                    <button
                      type="button"
                      class="tbl-kebab"
                      data-bs-toggle="dropdown"
                      data-bs-display="static"
                      aria-expanded="false"
                      aria-label="Table actions"
                    >
                      <i class="bx bx-dots-horizontal-rounded" aria-hidden="true"></i>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end">
                      <li>
                        <a class="dropdown-item type-option" (click)="addNewTable(table)">
                          <i class="bx bx-edit-alt menu-icon" aria-hidden="true"></i>
                          Edit Table
                        </a>
                      </li>
                      @if (table.status !== 'available') {
                        <li>
                          <a
                            class="dropdown-item type-option tbl-item-success"
                            (click)="markAsAvailable(table)"
                          >
                            <i class="bx bx-check-circle menu-icon" aria-hidden="true"></i>
                            Mark as Available
                          </a>
                        </li>
                      }
                      <li>
                        <a
                          class="dropdown-item type-option delete-dropdown-item"
                          (click)="deleteTableConfirm(table)"
                        >
                          <i class="bx bx-trash menu-icon" aria-hidden="true"></i>
                          Delete Table
                        </a>
                      </li>
                    </ul>
                  </div>
                </header>

                <dl class="tbl-card-rows">
                  <div class="tbl-card-row">
                    <dt>Shape</dt>
                    <dd>
                      <span class="tbl-shape">
                        <i class="bx" [ngClass]="shapeIcon(table)" aria-hidden="true"></i>
                        {{ shapeLabel(table) }}
                      </span>
                    </dd>
                  </div>
                  @if (showStatusColumn) {
                    <div class="tbl-card-row">
                      <dt>Status</dt>
                      <dd>
                        <span
                          class="tbl-status"
                          [ngClass]="'tbl-status--' + (table.status || 'unknown')"
                        >
                          <span class="tbl-status-dot" aria-hidden="true"></span>
                          {{ table.status | titlecase }}
                        </span>
                      </dd>
                    </div>
                  }
                  @if (showZoneColumn) {
                    <div class="tbl-card-row">
                      <dt>Zone</dt>
                      <dd>{{ table.zone?.zone_name || '—' }}</dd>
                    </div>
                  }
                  <div class="tbl-card-row">
                    <dt>Waiter</dt>
                    <dd>{{ waiterLabel(table) }}</dd>
                  </div>
                </dl>

                <footer class="tbl-card-foot">
                  <span class="tbl-card-qr">
                    <i class="bx bx-qr" aria-hidden="true"></i> QR Code
                  </span>
                  <button
                    type="button"
                    class="tbl-link"
                    data-bs-toggle="modal"
                    data-bs-target="#exampleModal"
                    (click)="viewQrCode(table)"
                  >
                    View QR
                  </button>
                </footer>
              </article>
            }
          </div>
        </section>
      }
    }
  }
} @else if (unFilteredTables?.length > 0) {
  <div class="d-flex flex-column align-items-center justify-content-center common-empty-container">
    <p class="common-empty-header">No matching tables</p>
    <p class="common-empty-sub-text">Try a different search term or filter.</p>
  </div>
} @else {
  <div class="d-flex flex-column align-items-center justify-content-center common-empty-container">
    <img src="assets/no-orders-yet.svg" alt="No tables" class="common-empty-img mb-3" />
    <p class="common-empty-header">No Tables</p>
    <p class="common-empty-sub-text">Create tables to manage your seating arrangements.</p>
  </div>
}

<!-- QR preview -->
<div
  class="modal fade"
  id="exampleModal"
  tabindex="-1"
  aria-labelledby="exampleModalLabel"
  aria-hidden="true"
>
  <div class="modal-dialog modal-dialog-centered tbl-qr-dialog">
    @if (selectedQrCode) {
      <div class="modal-content tbl-qr-card">
        <div class="tbl-qr-body">
          <img #qrCodeImage [src]="selectedQrCode.qr_code_path" alt="QR Code" class="qrCodeLarge" />
          <p class="tbl-qr-name">{{ selectedQrCode?.table_name }}</p>
          <p class="tbl-qr-hint">Diners scan this to open the table's menu.</p>
        </div>
        <div class="tbl-qr-foot">
          <button type="button" class="tbl-qr-action" (click)="downloadImage(selectedQrCode)">
            <i class="bx bx-image" aria-hidden="true"></i> Image
          </button>
          <button type="button" class="tbl-qr-action" (click)="downloadAsPDF(selectedQrCode)">
            <i class="bx bx-note" aria-hidden="true"></i> PDF
          </button>
          <button type="button" class="tbl-qr-action" (click)="printQRCode(selectedQrCode)">
            <i class="bx bx-printer" aria-hidden="true"></i> Print
          </button>
          <button type="button" class="tbl-qr-close" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    }
  </div>
</div>
